import axios, { type AxiosInstance, type AxiosResponse } from "axios";
import {
	clearTokens,
	getAccessToken,
	getRefreshToken,
	setTokens,
} from "../services/tokenStorage";

// API Response types
export interface ApiResponse<T = any> {
	success: boolean;
	data?: T;
	error?: string;
	message?: string;
}

// Create axios instance with default config
const apiClient: AxiosInstance = axios.create({
	baseURL: import.meta.env.VITE_API_BASE_URL || "http://localhost:3001",
	timeout: 30000,
	withCredentials: true, // Include cookies for session auth
	headers: {
		"Content-Type": "application/json",
	},
});

// Request interceptor for auth headers if needed
apiClient.interceptors.request.use(
	(config) => {
		const token = getAccessToken();
		if (token) {
			config.headers = config.headers ?? {};
			config.headers.Authorization = `Bearer ${token}`;
		}
		return config;
	},
	(error) => {
		return Promise.reject(error);
	},
);

// Response interceptor for error handling and token refresh
let isRefreshing = false;
let failedQueue: Array<{
	resolve: (value?: any) => void;
	reject: (error?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
	failedQueue.forEach((prom) => {
		if (error) {
			prom.reject(error);
		} else {
			prom.resolve(token);
		}
	});
	failedQueue = [];
};

apiClient.interceptors.response.use(
	(response: AxiosResponse) => {
		return response;
	},
	async (error) => {
		const originalRequest = error.config;

		// If error is 401 and we haven't tried to refresh yet
		if (error.response?.status === 401 && !originalRequest._retry) {
			if (isRefreshing) {
				// If already refreshing, queue this request
				return new Promise((resolve, reject) => {
					failedQueue.push({ resolve, reject });
				})
					.then((token) => {
						originalRequest.headers.Authorization = `Bearer ${token}`;
						return apiClient(originalRequest);
					})
					.catch((err) => {
						return Promise.reject(err);
					});
			}

			originalRequest._retry = true;
			isRefreshing = true;

			try {
				// Try to refresh token
				const refreshToken = getRefreshToken();
				if (!refreshToken) {
					throw new Error("No refresh token");
				}

				const response = await apiClient.post("/auth/refresh", {
					refreshToken,
				});

				const { accessToken, refreshToken: newRefreshToken } =
					response.data.data || response.data;

				if (accessToken && newRefreshToken) {
					// Always store the new refresh token - replaces old one (single token per user)
					setTokens({
						accessToken,
						refreshToken: newRefreshToken, // New token from server replaces old one
					});

					// Update the original request with new token
					originalRequest.headers.Authorization = `Bearer ${accessToken}`;

					// Process queued requests
					processQueue(null, accessToken);

					// Retry the original request
					return apiClient(originalRequest);
				} else {
					throw new Error("Invalid token response");
				}
			} catch (refreshError: any) {
				// Refresh failed - clear tokens and redirect to login
				processQueue(refreshError, null);
				clearTokens();

				// Log the error for debugging
				const errorMessage =
					refreshError?.response?.data?.error?.message ||
					refreshError?.response?.data?.message ||
					refreshError?.message ||
					"Session expired. Please log in again.";

				console.warn("[Auth] Token refresh failed:", errorMessage);

				// Only redirect if not already on login page
				if (!window.location.pathname.includes("/login")) {
					window.location.href = "/login";
				}

				return Promise.reject(refreshError);
			} finally {
				isRefreshing = false;
			}
		}

		// For other errors, just reject
		return Promise.reject(error);
	},
);

// Helper functions for common HTTP methods
export const api = {
	get: <T = any>(
		url: string,
		params?: any,
	): Promise<AxiosResponse<ApiResponse<T>>> => {
		return apiClient.get(url, { params });
	},

	post: <T = any>(
		url: string,
		data?: any,
	): Promise<AxiosResponse<ApiResponse<T>>> => {
		return apiClient.post(url, data);
	},

	put: <T = any>(
		url: string,
		data?: any,
	): Promise<AxiosResponse<ApiResponse<T>>> => {
		return apiClient.put(url, data);
	},

	patch: <T = any>(
		url: string,
		data?: any,
	): Promise<AxiosResponse<ApiResponse<T>>> => {
		return apiClient.patch(url, data);
	},

	delete: <T = any>(url: string): Promise<AxiosResponse<ApiResponse<T>>> => {
		return apiClient.delete(url);
	},
};

export default apiClient;
